import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, ArrowLeft, Edit2, Trash2, ChevronDown, ChevronUp, Lock, Unlock, X, Upload, FileText, Image as ImageIcon, BookOpen, Sparkles, CheckCircle, Eye, ClipboardList, Users, Settings, AlertTriangle, RefreshCw, BarChart3 } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const BASE_GRADES = [
    "1ro Primaria", "2do Primaria", "3ro Primaria", "4to Primaria", "5to Primaria", "6to Primaria",
    "1ro Secundaria", "2do Secundaria", "3ro Secundaria", "4to Secundaria", "5to Secundaria",
];

const GRADE_SECTIONS = BASE_GRADES.flatMap((g) =>
    (g === "5to Secundaria" ? ["A", "B", "C"] : ["A", "B"]).map((s) => `${g} ${s}`)
);

const DELIVERY_TYPE_LABELS = {
    pdf: "Archivo PDF",
    photo: "Foto",
    text: "Texto escrito",
};

// ─── Badge de estado de la IA ───
function AiStatusBadge({ status }) {
    if (status === "ready") {
        return (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                <Sparkles size={9} /> IA lista
            </span>
        );
    }
    if (status === "generating") {
        return (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 inline-flex items-center gap-1">
                <RefreshCw size={9} className="animate-spin" /> Generando…
            </span>
        );
    }
    if (status === "partial") {
        return (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 inline-flex items-center gap-1" title="Faltan versiones por generar">
                <AlertTriangle size={9} /> IA incompleta
            </span>
        );
    }
    if (status === "failed") {
        return (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 inline-flex items-center gap-1">
                <AlertTriangle size={9} /> IA falló
            </span>
        );
    }
    // Sin versiones generadas nunca
    return (
        <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 inline-flex items-center gap-1">
            Sin IA
        </span>
    );
}

export default function CursoDetalle() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [units, setUnits] = useState([]);
    const [lessons, setLessons] = useState([]);
    const [exams, setExams] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [creatingLesson, setCreatingLesson] = useState(null);
    const [editingLesson, setEditingLesson] = useState(null);
    const [creatingUnit, setCreatingUnit] = useState(false);
    const [editingUnit, setEditingUnit] = useState(null);
    const [generatingExam, setGeneratingExam] = useState(null);
    const [previewExam, setPreviewExam] = useState(null);
    const [regeneratingAi, setRegeneratingAi] = useState(null);
    const [viewingAssignments, setViewingAssignments] = useState(null); // lección cuyas tareas/entregas se están viendo

    async function load() {
        const [cr, ur, lr, er] = await Promise.all([
            api.get("/courses"),
            api.get("/lessons/units"),
            api.get("/lessons"),
            api.get("/exams"),
        ]);
        const found = (cr.data || []).find((c) => c.id === id);
        setCourse(found || null);
        setUnits((ur.data || []).filter((u) => u.subject === found?.name && (found?.sections || []).includes(u.grade)));
        setLessons((lr.data || []).filter((l) => l.subject === found?.name && (found?.sections || []).includes(l.grade)));
        setExams(er.data || []);
    }

    useEffect(() => { load(); }, [id]);

    // Refresco automático mientras haya lecciones generando versiones IA
    useEffect(() => {
        const hayGenerando = lessons.some((l) => l.versions_status === "generating");
        if (!hayGenerando) return;
        const timer = setInterval(() => { load(); }, 15000);
        return () => clearInterval(timer);
    }, [lessons]);

    async function toggleUnit(unitId, active) {
        await api.post(`/lessons/units/${unitId}/${active ? "deactivate" : "activate"}`);
        toast.success(active ? "Unidad desactivada" : "Unidad activada");
        load();
    }

    async function deleteLesson(lessonId) {
        if (!window.confirm("¿Eliminar lección?")) return;
        await api.delete(`/lessons/${lessonId}`);
        toast.success("Lección eliminada");
        load();
    }

    async function deleteUnit(unit) {
        if (!unit?.id) return;
        if (!window.confirm(`¿Eliminar la Unidad ${unit.unit_number}: ${unit.unit_name || ""}?`)) return;
        try {
            await api.delete(`/lessons/units/${unit.id}`);
            toast.success("Unidad eliminada");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error eliminando unidad");
        }
    }

    async function generateVersions(lesson) {
        setRegeneratingAi(lesson.id);
        try {
            await api.post(`/ai/lessons/${lesson.id}/generate-versions`);
            toast.success("✅ Versiones IA generadas");
        } catch (e) {
            if (e.response?.status === 409) {
                toast.info("Esta lección ya se está generando. Espera a que termine.");
            } else {
                toast.error(e.response?.data?.detail || "Error generando versiones IA");
            }
        } finally {
            setRegeneratingAi(null);
            load();
        }
    }

    async function generateExam(lesson) {
        setGeneratingExam(lesson.id);
        try {
            const { data } = await api.post(`/ai/lessons/${lesson.id}/generate-exam`);
            toast.success("✅ Examen generado — revísalo y apruébalo");
            await load();
            setPreviewExam(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error generando examen");
        } finally {
            setGeneratingExam(null);
        }
    }

    async function approveExam(examId) {
        try {
            await api.post(`/exams/${examId}/approve`);
            toast.success("✅ Examen aprobado — ya visible para alumnos");
            setPreviewExam(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error aprobando examen");
        }
    }

    async function deleteExam(examId) {
        try {
            await api.delete(`/exams/${examId}`);
            toast.success("Examen eliminado");
            load();
        } catch {
            toast.error("Error eliminando examen");
        }
    }

    if (!course) return <div className="p-10 text-edu-muted">Cargando...</div>;

    const unitNumbers = [...new Set(units.map((u) => u.unit_number))].sort((a, b) => a - b);
    const nextUnitNumber = unitNumbers.length > 0 ? Math.max(...unitNumbers) + 1 : 1;

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen flex flex-col">
            <button onClick={() => navigate("/profesor/mis-cursos")} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6 flex-shrink-0">
                <ArrowLeft size={16} /> Volver a Mis Cursos
            </button>

            <div className="flex items-center justify-between mb-8 flex-shrink-0">
                <div>
                    <div className="w-3 h-3 rounded-full inline-block mr-2" style={{ background: course.color }} />
                    <h1 className="text-3xl font-semibold inline">{course.name}</h1>
                    <p className="text-edu-body mt-1">{course.description}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {(course.sections || []).map((s) => (
                            <span key={s} className="edu-pill bg-edu-navy/10 text-edu-navy">{s}</span>
                        ))}
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => navigate(`/profesor/cursos/${id}/reporte-errores`)} className="px-4 py-2.5 text-sm inline-flex items-center gap-2 rounded-xl border border-edu hover:bg-edu-soft">
                        <BarChart3 size={14} /> Reporte de errores
                    </button>
                    <button onClick={() => setCreatingUnit(true)} className="px-4 py-2.5 text-sm inline-flex items-center gap-2 rounded-xl border border-edu hover:bg-edu-soft">
                        <Plus size={14} /> Nueva Unidad
                    </button>
                    <button onClick={() => setCreatingLesson({ unit_number: nextUnitNumber })} className="edu-btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2">
                        <Plus size={14} /> Nueva Lección
                    </button>
                </div>
            </div>

            {unitNumbers.length === 0 && (
                <div className="edu-card p-10 text-center text-edu-muted flex-1">No hay unidades aún. Crea la primera unidad.</div>
            )}

            <div className="space-y-4 flex-1">
                {unitNumbers.map((num) => {
                    const unit = units.find((u) => u.unit_number === num);
                    const unitLessons = lessons.filter((l) => l.unit_number === num);
                    const isExpanded = expanded[num] !== false;
                    return (
                        <div key={num} className="edu-card overflow-hidden">
                            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-edu-soft" onClick={() => setExpanded((e) => ({ ...e, [num]: !isExpanded }))}>
                                <div className="flex items-center gap-3">
                                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    <span className="font-semibold">Unidad {num}: {unit?.unit_name || ""}</span>
                                    <span className="text-xs text-edu-muted">{unitLessons.length} lección{unitLessons.length !== 1 ? "es" : ""}</span>
                                </div>
                                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                    <button onClick={() => toggleUnit(unit?.id, unit?.active)} className={`px-3 py-1 text-xs rounded-lg inline-flex items-center gap-1 ${unit?.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                                        {unit?.active ? <Unlock size={12} /> : <Lock size={12} />}
                                        {unit?.active ? "Activa" : "Bloqueada"}
                                    </button>
                                    <button onClick={() => setEditingUnit(unit)} className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                                        <Settings size={12} /> Editar Unidad
                                    </button>
                                    <button onClick={() => setCreatingLesson({ unit_number: num, unit_name: unit?.unit_name })} className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                                        <Plus size={12} /> Lección
                                    </button>
                                    <button onClick={() => deleteUnit(unit)} className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 hover:bg-red-200 inline-flex items-center gap-1">
                                        <Trash2 size={12} /> Eliminar Unidad
                                    </button>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="border-t border-edu divide-y divide-edu/50">
                                    {unitLessons.length === 0 && <div className="px-4 py-3 text-sm text-edu-muted">Sin lecciones.</div>}
                                    {unitLessons.map((l) => {
                                        const lessonExam = exams.find((e) => e.lesson_id === l.id);
                                        const isGenerating = generatingExam === l.id;
                                        const aiStatus = l.versions_status;
                                        const aiReady = aiStatus === "ready";
                                        const aiWorking = aiStatus === "generating" || regeneratingAi === l.id;
                                        const necesitaIa = !aiReady && !aiWorking;
                                        return (
                                            <div key={l.id} className="px-4 py-3">
                                                <div className="flex items-center justify-between flex-wrap gap-2">
                                                    <div>
                                                        <p className="font-medium text-sm">{l.title}</p>
                                                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                            <p className="text-xs text-edu-muted capitalize">{l.grade}</p>
                                                            <AiStatusBadge status={aiStatus} />
                                                            {lessonExam && (
                                                                <span className={`text-xs px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 ${lessonExam.approved ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>
                                                                    {lessonExam.approved ? <><CheckCircle size={9} /> Examen activo</> : "⏳ Pendiente de aprobación"}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2 flex-wrap justify-end">
                                                        {necesitaIa && (
                                                            <button
                                                                onClick={() => generateVersions(l)}
                                                                disabled={regeneratingAi === l.id}
                                                                className="px-3 py-1 text-xs rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200 disabled:opacity-40 inline-flex items-center gap-1 transition-colors"
                                                            >
                                                                <RefreshCw size={12} className={regeneratingAi === l.id ? "animate-spin" : ""} />
                                                                {aiStatus ? "Reintentar IA" : "Generar versiones IA"}
                                                            </button>
                                                        )}
                                                        {!lessonExam ? (
                                                            <button onClick={() => generateExam(l)} disabled={isGenerating || !aiReady} title={!aiReady ? "Espera a que la IA esté lista" : ""} className="px-3 py-1 text-xs rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-40 inline-flex items-center gap-1 transition-colors">
                                                                <Sparkles size={12} />
                                                                {isGenerating ? "Generando…" : "Generar examen IA"}
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button onClick={() => setPreviewExam(lessonExam)} className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                                                                    <Eye size={12} /> Ver examen
                                                                </button>
                                                                {!lessonExam.approved && (
                                                                    <button onClick={() => approveExam(lessonExam.id)} className="px-3 py-1 text-xs rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 inline-flex items-center gap-1">
                                                                        <CheckCircle size={12} /> Aprobar
                                                                    </button>
                                                                )}
                                                                <button onClick={() => deleteExam(lessonExam.id)} className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 hover:bg-red-200 inline-flex items-center gap-1">
                                                                    <Trash2 size={12} /> Borrar examen
                                                                </button>
                                                            </>
                                                        )}
                                                        <button onClick={() => setViewingAssignments(l)} className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                                                            <ClipboardList size={12} /> Tareas y entregas
                                                        </button>
                                                        <button onClick={() => setEditingLesson(l)} className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                                                            <Edit2 size={12} /> Editar
                                                        </button>
                                                        <button onClick={() => deleteLesson(l.id)} className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1">
                                                            <Trash2 size={12} /> Eliminar
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {creatingUnit && <UnitModal nextNumber={nextUnitNumber} subject={course.name} sections={course.sections || []} onClose={() => setCreatingUnit(false)} onSaved={() => { setCreatingUnit(false); load(); }} />}
            {editingUnit && <UnitEditModal unit={editingUnit} onClose={() => setEditingUnit(null)} onSaved={() => { setEditingUnit(null); load(); }} />}
            {(creatingLesson || editingLesson) && (
                <LessonModal lesson={editingLesson} defaultUnit={creatingLesson} subject={course.name} sections={course.sections || []}
                    onClose={() => { setCreatingLesson(null); setEditingLesson(null); }}
                    onSaved={() => { setCreatingLesson(null); setEditingLesson(null); load(); }}
                />
            )}
            {previewExam && <ExamPreviewModal exam={previewExam} onClose={() => { setPreviewExam(null); load(); }} onApprove={() => approveExam(previewExam.id)} />}
            {viewingAssignments && <LessonAssignmentsModal lesson={viewingAssignments} onClose={() => setViewingAssignments(null)} />}
        </div>
    );
}

// ─── LessonAssignmentsModal ─── acceso directo a tareas/entregas de una lección, sin pasar por "Editar Lección"
function LessonAssignmentsModal({ lesson, onClose }) {
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="edu-card p-6 max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                    <div>
                        <h3 className="text-xl font-semibold">{lesson.title}</h3>
                        <p className="text-sm text-edu-muted">Tareas y entregas de esta lección</p>
                    </div>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="overflow-y-auto pr-1 flex-1 custom-scrollbar">
                    <AssignmentsSection lessonId={lesson.id} />
                </div>
                <div className="flex justify-end pt-4 border-t border-edu/40 flex-shrink-0 mt-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cerrar</button>
                </div>
            </div>
        </div>
    );
}

// ─── ExamPreviewModal ───
function ExamPreviewModal({ exam, onClose, onApprove }) {
    const [editing, setEditing] = useState(false);
    const [questions, setQuestions] = useState(exam.questions || []);
    const [saving, setSaving] = useState(false);

    async function saveChanges() {
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (!q.question.trim()) { toast.error(`La pregunta ${i + 1} no puede estar vacía`); return; }
            for (let oi = 0; oi < q.options.length; oi++) {
                if (!q.options[oi].trim()) { toast.error(`La opción ${oi + 1} de la pregunta ${i + 1} no puede estar vacía`); return; }
            }
        }
        setSaving(true);
        try {
            await api.patch(`/exams/${exam.id}`, { questions });
            toast.success("Examen actualizado");
            setEditing(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error guardando cambios");
        } finally {
            setSaving(false);
        }
    }

    function updateQuestion(qi, field, value) {
        setQuestions((prev) => prev.map((q, i) => i === qi ? { ...q, [field]: value } : q));
    }

    function updateOption(qi, oi, value) {
        setQuestions((prev) => prev.map((q, i) => {
            if (i !== qi) return q;
            const opts = [...q.options];
            opts[oi] = value;
            return { ...q, options: opts };
        }));
    }

    function addQuestion() {
        setQuestions((prev) => [...prev, { question: "", options: ["", "", "", ""], correct_index: 0 }]);
        setTimeout(() => {
            const modal = document.getElementById("exam-modal-scroll");
            if (modal) modal.scrollTop = modal.scrollHeight;
        }, 50);
    }

    function removeQuestion(qi) {
        if (questions.length <= 1) { toast.error("El examen debe tener al menos una pregunta"); return; }
        setQuestions((prev) => prev.filter((_, i) => i !== qi));
    }

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div id="exam-modal-scroll" className="edu-card p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-xl font-semibold">{exam.title}</h3>
                        <p className="text-sm text-edu-muted">{exam.subject} · {exam.grade} · {questions.length} preguntas · {exam.coin_cost} monedas</p>
                    </div>
                    <button onClick={onClose}><X size={18} /></button>
                </div>

                <div className="space-y-4 mb-4">
                    {questions.map((q, qi) => (
                        <div key={qi} className="border border-edu rounded-xl p-4">
                            {editing ? (
                                <>
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-xs text-edu-muted font-semibold uppercase tracking-widest">Pregunta {qi + 1}</p>
                                        <button onClick={() => removeQuestion(qi)} className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"><Trash2 size={11} /> Eliminar</button>
                                    </div>
                                    <textarea value={q.question} onChange={(e) => updateQuestion(qi, "question", e.target.value)} rows={2} placeholder="Escribe la pregunta aquí…" className="edu-input w-full text-sm font-medium mb-3" />
                                    <p className="text-xs text-edu-muted mb-2">Edita las opciones y selecciona (●) la respuesta correcta:</p>
                                    <div className="space-y-2">
                                        {q.options.map((opt, oi) => (
                                            <div key={oi} className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${oi === q.correct_index ? "border-emerald-300 bg-emerald-50" : "border-edu bg-gray-50"}`}>
                                                <input type="radio" name={`correct-${qi}`} checked={oi === q.correct_index} onChange={() => updateQuestion(qi, "correct_index", oi)} className="accent-emerald-600 flex-shrink-0 cursor-pointer" />
                                                <input type="text" value={opt} onChange={(e) => updateOption(qi, oi, e.target.value)} placeholder={`Opción ${oi + 1}`} className="bg-transparent border-none outline-none text-sm w-full" />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="font-medium text-sm mb-3">{qi + 1}. {q.question}</p>
                                    <div className="space-y-1.5">
                                        {q.options.map((opt, oi) => (
                                            <div key={oi} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${oi === q.correct_index ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-gray-50 text-edu-body"}`}>
                                                {oi === q.correct_index && <CheckCircle size={14} className="text-emerald-600 flex-shrink-0" />}
                                                <span>{opt}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {editing && (
                    <button onClick={addQuestion} className="w-full py-2.5 text-sm rounded-xl border-2 border-dashed border-edu hover:border-edu-navy hover:bg-edu-soft transition-colors inline-flex items-center justify-center gap-2 text-edu-muted hover:text-edu-navy mb-4">
                        <Plus size={14} /> Agregar pregunta
                    </button>
                )}

                <div className="flex items-center justify-between gap-3 border-t border-edu pt-4">
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cerrar</button>
                        {!editing ? (
                            <button onClick={() => setEditing(true)} className="px-4 py-2 text-sm rounded-xl border border-edu inline-flex items-center gap-2 hover:bg-edu-soft">
                                <Edit2 size={14} /> Editar
                            </button>
                        ) : (
                            <>
                                <button onClick={() => { setQuestions(exam.questions || []); setEditing(false); }} className="px-4 py-2 text-sm rounded-xl border border-edu hover:bg-edu-soft">Cancelar</button>
                                <button onClick={saveChanges} disabled={saving} className="px-4 py-2 text-sm rounded-xl bg-edu-navy text-white hover:opacity-90 inline-flex items-center gap-2 disabled:opacity-50">
                                    {saving ? "Guardando…" : "Guardar cambios"}
                                </button>
                            </>
                        )}
                    </div>
                    {!exam.approved && !editing && (
                        <button onClick={onApprove} className="edu-btn-primary px-4 py-2 text-sm inline-flex items-center gap-2">
                            <CheckCircle size={14} /> Aprobar y publicar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── UnitModal ───
function UnitModal({ nextNumber, subject, sections, onClose, onSaved }) {
    const [unitNumber, setUnitNumber] = useState(nextNumber);
    const [unitName, setUnitName] = useState("");
    const [saving, setSaving] = useState(false);

    async function save() {
        if (!unitName.trim()) { toast.error("Ingresá el nombre de la unidad"); return; }
        setSaving(true);
        try {
            await api.post("/lessons/units", { subject, grade: sections[0] || "", unit_number: unitNumber, unit_name: unitName });
            toast.success("Unidad creada");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al crear unidad");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="edu-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">Nueva Unidad</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Nº Unidad</span>
                        <input type="number" min={1} max={20} value={unitNumber} onChange={(e) => setUnitNumber(parseInt(e.target.value))} className="edu-input w-full" />
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Nombre de la Unidad</span>
                        <input placeholder="ej: Los Números Fraccionarios" value={unitName} onChange={(e) => setUnitName(e.target.value)} className="edu-input w-full" />
                    </div>
                    <p className="text-xs text-edu-muted bg-edu-soft rounded-xl p-3">
                        La unidad se creará vacía. Luego agrégale lecciones con el botón «+ Lección».
                    </p>
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                        <button onClick={save} disabled={saving || !unitName.trim()} className="edu-btn-primary px-4 py-2 text-sm">{saving ? "Guardando…" : "Crear Unidad"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── UnitEditModal ───
function UnitEditModal({ unit, onClose, onSaved }) {
    const [unitNumber, setUnitNumber] = useState(unit.unit_number);
    const [unitName, setUnitName] = useState(unit.unit_name || "");
    const [saving, setSaving] = useState(false);

    async function save() {
        if (!unitName.trim()) { toast.error("Ingresá el nombre de la unidad"); return; }
        setSaving(true);
        try {
            await api.patch(`/lessons/units/${unit.id}`, { unit_number: unitNumber, unit_name: unitName });
            toast.success("Unidad actualizada");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al actualizar unidad");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="edu-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">Editar Unidad</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Nº Unidad</span>
                        <input type="number" min={1} max={20} value={unitNumber} onChange={(e) => setUnitNumber(parseInt(e.target.value))} className="edu-input w-full" />
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Nombre de la Unidad</span>
                        <input placeholder="ej: Los Números Fraccionarios" value={unitName} onChange={(e) => setUnitName(e.target.value)} className="edu-input w-full" />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                        <button onClick={save} disabled={saving || !unitName.trim()} className="edu-btn-primary px-4 py-2 text-sm">{saving ? "Guardando…" : "Guardar"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── LessonModal ───
function LessonModal({ lesson, defaultUnit, subject, sections, onClose, onSaved }) {
    const isEdit = !!lesson;
    const [form, setForm] = useState(lesson || {
        title: "", description: "", subject, grade: sections[0] || "",
        unit_number: defaultUnit?.unit_number || 1, unit_name: defaultUnit?.unit_name || "",
        learning_style: "visual", coin_cost: 0, youtube_url: "", learning_outcomes: "",
    });
    const [saving, setSaving] = useState(false);
    const [uploadingSummary, setUploadingSummary] = useState(false);
    const [uploadingWhiteboard, setUploadingWhiteboard] = useState(false);
    const [uploadingActivities, setUploadingActivities] = useState(false);

    async function uploadFile(field, file, setLoading) {
        if (!isEdit) { toast.error("Primero guarda la lección, luego sube materiales"); return; }
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch(`/lessons/${lesson.id}`, { [field]: data.id });
            toast.success("Material subido");
            setForm((f) => ({ ...f, [field]: data.id }));
        } catch {
            toast.error("Error al subir el archivo");
        } finally {
            setLoading(false);
        }
    }

    async function removeFile(field) {
        if (!isEdit) return;
        try {
            await api.patch(`/lessons/${lesson.id}`, { [field]: null });
            setForm((f) => ({ ...f, [field]: null }));
            toast.success("Material eliminado");
        } catch {
            toast.error("Error al eliminar");
        }
    }

    async function save() {
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/lessons/${lesson.id}`, form);
                toast.success("Lección actualizada");
            } else {
                const { data: newLesson } = await api.post("/lessons", { ...form, subject });
                toast.success("Lección creada");
                onSaved();
                toast.info("✨ Generando versiones IA personalizadas…", { duration: 4000 });
                api.post(`/ai/lessons/${newLesson.id}/generate-versions`)
                    .then(() => toast.success("✅ Versiones IA listas"))
                    .catch(() => toast.error("Error generando versiones IA"));
                return;
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="edu-card p-6 max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                    <h3 className="text-xl font-semibold">{isEdit ? "Editar Lección" : "Nueva Lección"}</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-4 overflow-y-auto pr-1 flex-1 custom-scrollbar">
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Título de la Lección</span>
                        <input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="edu-input w-full" />
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Descripción</span>
                        <textarea placeholder="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="edu-input w-full" />
                    </div>
                    {!isEdit && (
    <>
        <div>
            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Sección / Grado</span>
            <select value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="edu-input w-full">
                {sections.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
            <div>
                <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Nº Unidad</span>
                <input type="number" min={1} max={20} value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: parseInt(e.target.value) })} className="edu-input w-full" />
            </div>
            <div>
                <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Nombre Unidad</span>
                <input placeholder="ej: Fracciones" value={form.unit_name} onChange={(e) => setForm({ ...form, unit_name: e.target.value })} className="edu-input w-full" />
            </div>
        </div>
    </>
)}
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">YouTube URL</span>
                        <input placeholder="https://youtube.com/..." value={form.youtube_url || ""} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} className="edu-input w-full" />
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Objetivos de aprendizaje</span>
                        <textarea placeholder="¿Qué aprenderá el alumno?" value={form.learning_outcomes || ""} onChange={(e) => setForm({ ...form, learning_outcomes: e.target.value })} rows={3} className="edu-input w-full" />
                    </div>
                    {isEdit && (
                        <div className="border-t border-edu pt-3 mt-2">
                            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-3 block">Materiales</span>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between p-3 rounded-xl bg-edu-soft">
                                    <div className="flex items-center gap-2 text-sm"><FileText size={16} className="text-edu-navy" /><span>Resumen PDF</span></div>
                                    {form.summary_file_id ? (
                                        <button onClick={() => removeFile("summary_file_id")} className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1"><Trash2 size={12} /> Eliminar</button>
                                    ) : (
                                        <label className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1 cursor-pointer">
                                            <Upload size={12} /> {uploadingSummary ? "Subiendo…" : "Subir PDF"}
                                            <input type="file" accept=".pdf" className="hidden" onChange={(e) => e.target.files[0] && uploadFile("summary_file_id", e.target.files[0], setUploadingSummary)} />
                                        </label>
                                    )}
                                </div>
                                <div className="flex items-center justify-between p-3 rounded-xl bg-edu-soft">
                                    <div className="flex items-center gap-2 text-sm"><ImageIcon size={16} className="text-edu-navy" /><span>Foto de pizarra</span></div>
                                    {form.whiteboard_file_id ? (
                                        <button onClick={() => removeFile("whiteboard_file_id")} className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1"><Trash2 size={12} /> Eliminar</button>
                                    ) : (
                                        <label className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1 cursor-pointer">
                                            <Upload size={12} /> {uploadingWhiteboard ? "Subiendo…" : "Subir imagen"}
                                            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadFile("whiteboard_file_id", e.target.files[0], setUploadingWhiteboard)} />
                                        </label>
                                    )}
                                </div>
                                <div className="flex items-center justify-between p-3 rounded-xl bg-edu-soft">
                                    <div className="flex items-center gap-2 text-sm"><BookOpen size={16} className="text-edu-navy" /><span>Actividades</span></div>
                                    {form.activities_file_id ? (
                                        <button onClick={() => removeFile("activities_file_id")} className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1"><Trash2 size={12} /> Eliminar</button>
                                    ) : (
                                        <label className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1 cursor-pointer">
                                            <Upload size={12} /> {uploadingActivities ? "Subiendo…" : "Subir archivo"}
                                            <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => e.target.files[0] && uploadFile("activities_file_id", e.target.files[0], setUploadingActivities)} />
                                        </label>
                                    )}
                                </div>
                            </div>
                            <p className="text-xs text-edu-muted mt-2">Los materiales se suben después de guardar la lección.</p>
                        </div>
                    )}
                    {!isEdit && (
                        <p className="text-xs text-edu-muted bg-edu-soft rounded-xl p-3">
                            💡 Después de crear la lección podrás subir materiales. La IA generará automáticamente versiones personalizadas.
                        </p>
                    )}
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-edu/40 flex-shrink-0 mt-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu text-edu-body hover:bg-edu-soft">Cancelar</button>
                    <button onClick={save} disabled={saving || !form.title} className="edu-btn-primary px-4 py-2 text-sm">{saving ? "Guardando…" : "Guardar"}</button>
                </div>
            </div>
        </div>
    );
}

// ─── AssignmentsSection ───
function AssignmentsSection({ lessonId }) {
    const navigate = useNavigate();
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/assignments/lesson/${lessonId}`);
            setAssignments(data || []);
        } catch {
            toast.error("Error cargando tareas");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, [lessonId]);

    async function deleteAssignment(id) {
        if (!window.confirm("¿Eliminar esta tarea? También se eliminarán las entregas de los alumnos.")) return;
        try {
            await api.delete(`/assignments/${id}`);
            toast.success("Tarea eliminada");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error eliminando tarea");
        }
    }

    return (
        <div className="border-t border-edu pt-3 mt-2">
            <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest inline-flex items-center gap-1.5">
                    <ClipboardList size={14} className="text-edu-navy" /> Tareas y evaluaciones
                </span>
                <button onClick={() => setCreating(true)} className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1">
                    <Plus size={12} /> Agregar tarea
                </button>
            </div>
            {loading ? (
                <p className="text-xs text-edu-muted">Cargando…</p>
            ) : assignments.length === 0 ? (
                <p className="text-xs text-edu-muted bg-edu-soft rounded-xl p-3">Aún no hay tareas para esta lección.</p>
            ) : (
                <div className="space-y-2">
                    {assignments.map((a) => (
                        <AssignmentRow key={a.id} assignment={a} onDeleted={() => deleteAssignment(a.id)} onSaved={load} onViewSubs={() => navigate(`/profesor/tareas/${a.id}`)} />
                    ))}
                </div>
            )}
            {creating && <AssignmentFormModal lessonId={lessonId} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
        </div>
    );
}

// ─── AssignmentRow ───
function AssignmentRow({ assignment, onDeleted, onSaved, onViewSubs }) {
    const [editing, setEditing] = useState(false);

    // Mostrar fecha y hora si existe
    const dueDateDisplay = assignment.due_date
        ? assignment.due_date.length > 10
            ? new Date(assignment.due_date).toLocaleString("es-PE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
            : assignment.due_date
        : "Sin fecha";

    return (
        <div className="rounded-xl bg-edu-soft p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <p className="font-medium text-sm">{assignment.title}</p>
                <div className="flex gap-2 flex-wrap justify-end">
                    <button onClick={onViewSubs} className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-white inline-flex items-center gap-1">
                        <Users size={12} /> {assignment.submission_count ?? 0} entregados
                    </button>
                    <button onClick={() => setEditing(true)} className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-white inline-flex items-center gap-1">
                        <Edit2 size={12} /> Editar
                    </button>
                    <button onClick={onDeleted} className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 hover:bg-red-200 inline-flex items-center gap-1">
                        <Trash2 size={12} /> Eliminar
                    </button>
                </div>
            </div>
            {assignment.instructions && <p className="text-xs text-edu-body mb-3">{assignment.instructions}</p>}
            <div className="grid grid-cols-3 gap-3">
                <div>
                    <span className="text-[10px] text-edu-muted font-semibold uppercase tracking-widest block">Tipo de entrega</span>
                    <span className="text-sm">{DELIVERY_TYPE_LABELS[assignment.delivery_type] || assignment.delivery_type}</span>
                </div>
                <div>
                    <span className="text-[10px] text-edu-muted font-semibold uppercase tracking-widest block">Fecha de entrega</span>
                    <span className="text-sm">{dueDateDisplay}</span>
                </div>
                <div>
                    <span className="text-[10px] text-edu-muted font-semibold uppercase tracking-widest block">Puntaje máximo</span>
                    <span className="text-sm">{assignment.max_score} pts</span>
                </div>
            </div>
            {editing && <AssignmentFormModal assignment={assignment} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); onSaved(); }} />}
        </div>
    );
}

// ─── AssignmentFormModal ───
function AssignmentFormModal({ lessonId, assignment, onClose, onSaved }) {
    const isEdit = !!assignment;

    // Normalizar due_date a formato datetime-local "YYYY-MM-DDTHH:mm"
    const normalizeDate = (val) => {
        if (!val) return "";
        if (val.length === 10) return val + "T00:00";  // solo fecha
        return val.slice(0, 16);                        // ya tiene hora
    };

    const [form, setForm] = useState(assignment ? {
        title: assignment.title || "",
        instructions: assignment.instructions || "",
        delivery_type: assignment.delivery_type || "pdf",
        due_date: normalizeDate(assignment.due_date),
        max_score: assignment.max_score ?? 20,
    } : {
        title: "",
        instructions: "",
        delivery_type: "pdf",
        due_date: "",
        max_score: 20,
    });
    const [saving, setSaving] = useState(false);
    const [rubric, setRubric] = useState(assignment ? { file_id: assignment.rubric_file_id, filename: assignment.rubric_filename } : { file_id: null, filename: null });
    const [uploadingRubric, setUploadingRubric] = useState(false);

    async function save() {
        if (!form.title.trim()) { toast.error("El título de la tarea es obligatorio"); return; }
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/assignments/${assignment.id}`, { title: form.title, instructions: form.instructions, delivery_type: form.delivery_type, due_date: form.due_date || null, max_score: form.max_score });
                toast.success("Tarea actualizada");
            } else {
                await api.post("/assignments", { lesson_id: lessonId, title: form.title, instructions: form.instructions, delivery_type: form.delivery_type, due_date: form.due_date || null, max_score: form.max_score });
                toast.success("Tarea creada");
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error guardando tarea");
        } finally {
            setSaving(false);
        }
    }

    async function uploadRubric(file) {
        if (!isEdit) return;
        setUploadingRubric(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/assignments/${assignment.id}/rubric`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setRubric({ file_id: data.rubric_file_id, filename: data.rubric_filename });
            toast.success("Rúbrica cargada — la IA la usará para comentar las próximas entregas");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo la rúbrica");
        } finally {
            setUploadingRubric(false);
        }
    }

    async function removeRubric() {
        if (!isEdit) return;
        try {
            await api.delete(`/assignments/${assignment.id}/rubric`);
            setRubric({ file_id: null, filename: null });
            toast.success("Rúbrica eliminada");
        } catch {
            toast.error("Error eliminando la rúbrica");
        }
    }

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
            <div className="edu-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">{isEdit ? "Editar tarea" : "Nueva tarea"}</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Título *</span>
                        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="edu-input w-full" placeholder="ej: Línea de tiempo - Combate de Angamos" />
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Instrucciones</span>
                        <textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={3} className="edu-input w-full" placeholder="Indicaciones para el alumno" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Tipo de entrega</span>
                            <select value={form.delivery_type} onChange={(e) => setForm({ ...form, delivery_type: e.target.value })} className="edu-input w-full">
                                <option value="pdf">Archivo PDF</option>
                                <option value="photo">Foto</option>
                                <option value="text">Texto escrito</option>
                            </select>
                        </div>
                        <div>
                            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Puntaje máximo</span>
                            <input type="number" value={form.max_score} onChange={(e) => setForm({ ...form, max_score: parseInt(e.target.value || "0", 10) })} className="edu-input w-full" />
                        </div>
                    </div>
                    <div>
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Fecha y hora de entrega</span>
                        <input
                            type="datetime-local"
                            value={form.due_date}
                            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                            className="edu-input w-full"
                        />
                    </div>

                    <div className="border-t border-edu pt-3">
                        <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Rúbrica (opcional)</span>
                        <p className="text-[11px] text-edu-muted mb-2">
                            Si subes una rúbrica, la IA además de gramática comentará el ensayo criterio por criterio (sin poner nota — eso lo decides tú).
                        </p>
                        {!isEdit ? (
                            <p className="text-xs text-edu-muted bg-edu-soft rounded-xl p-3">Guarda la tarea primero; luego podrás subir la rúbrica.</p>
                        ) : rubric.file_id ? (
                            <div className="flex items-center justify-between p-3 rounded-xl bg-edu-soft">
                                <div className="flex items-center gap-2 text-sm min-w-0">
                                    <FileText size={16} className="text-edu-navy flex-shrink-0" />
                                    <a href={fileUrl(rubric.file_id)} target="_blank" rel="noreferrer" className="truncate hover:underline">{rubric.filename}</a>
                                </div>
                                <button onClick={removeRubric} className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1 flex-shrink-0"><Trash2 size={12} /> Eliminar</button>
                            </div>
                        ) : (
                            <label className="px-3 py-1.5 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1 cursor-pointer">
                                <Upload size={12} /> {uploadingRubric ? "Subiendo…" : "Subir rúbrica (PDF/Word)"}
                                <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" disabled={uploadingRubric} onChange={(e) => e.target.files[0] && uploadRubric(e.target.files[0])} />
                            </label>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                        <button onClick={save} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm">{saving ? "Guardando…" : "Guardar"}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
